import { useState } from 'react';
import { api, useApi, type ProfileEstimate, type SpendSummary } from '../api';
import { CREDITS_LABEL, formatCredits, formatUsd } from '../units';
import {
  Button,
  Card,
  ErrorBanner,
  Loading,
  QuotaBar,
  TablaResponsiva,
  formatDateTime,
} from '../components/ui';
import { useT, type Clave } from '../i18n';

type UsageData = {
  spend: SpendSummary;
  quota: {
    limit: number;
    used: number;
    remaining: number;
    resetsAt: string;
    /** El proveedor cortó, por encima de lo que estima el ledger. */
    providerExhausted: boolean;
    byOperation: { operation: string; used: number; limit: number }[];
  };
  byProvider: {
    provider: string;
    model: string;
    /** `image`, `video`, `text`, `vision`… Lo que vuelve legible la tabla. */
    operation: string;
    neurons: number;
    costUsd: number;
    calls: number;
    failures: number;
  }[];
  deferred: { id: string; operation: string; reason: string; runAfter: string }[];
  profile: string;
  /** Costo de cada perfil, calculado por el servidor. La UI nunca escribe estos números. */
  profiles: ProfileEstimate[];
  /** Dónde vive la media y cuánto ocupa. `objects`/`bytes` sólo con KV. */
  media: { backend: 'r2' | 'kv' | 'none'; objects?: number; bytes?: number };
};

type SettingsData = {
  /**
   * Sólo los tres que esta pantalla lee.
   *
   * El resto —qué modelo hace cada cosa— se elige en Modelos, y su forma se declara allá. Repetir
   * acá el tipo entero invitaba a lo que ya pasó: los campos quedaron declarados después de que
   * los desplegables se fueran, y nada avisó.
   */
  settings: {
    zeroSpendMode: boolean;
    dailyNeuronLimit: number;
    monthlyBudgetUsd: number;
  };
  flags: { key: string; enabled: boolean; description: string; canIncurCost: boolean }[];
};

/**
 * Consumo y ajustes (§19).
 *
 * Reúne el estado de las cuotas, el consumo por proveedor y los interruptores del sistema.
 * Desactivar el modo USD 0 vive acá y sólo acá, detrás de una confirmación explícita.
 *
 * ── Qué NO está acá ───────────────────────────────────────────────────────────
 * Con qué modelo se genera cada cosa: eso vive en Modelos. Son dos preguntas distintas —«cuánto
 * se gastó», que se mira, y «con qué se genera», que se decide— y juntas no se podía hacer
 * ninguna de las dos: para cambiar un modelo había que pasar por encima de tres tablas de
 * consumo, y para leer el gasto había que esquivar cinco desplegables.
 */
/**
 * Texto de cada perfil.
 *
 * Sólo lo que una persona necesita leer: los números —costo por influencer, influencers por
 * día, si admite referencias— los calcula el servidor con el mismo estimador que frena la
 * generación. Escritos acá quedarían desactualizados en cuanto cambie una tarifa, y una
 * cifra vieja en una pantalla de decisiones es peor que ninguna cifra.
 */
const PROFILE_COPY: Record<
  string,
  {
    label: string;
    short: string;
    provider: string;
    models: Clave;
    recommended?: boolean;
    caveat?: Clave;
  }
> = {
  economy: {
    label: 'Economy',
    /**
     * El modelo, en la propia opción de la lista.
     *
     * «Economy» y «Balanced» no dicen nada de lo que se va a usar, y el nombre engaña
     * —Economy es el más caro de los que sirven—. Para elegir hay que saber qué modelo entra,
     * y eso estaba sólo en el detalle, que aparece después de haber elegido.
     */
    short: 'FLUX.1 schnell',
    provider: 'Cloudflare',
    models: 'perfil.economyModelos',
  },
  balanced: {
    label: 'Balanced',
    short: 'FLUX.2 Klein 4B',
    provider: 'Cloudflare',
    models: 'perfil.balancedModelos',
    recommended: true,
  },
  premium: {
    label: 'Premium',
    short: 'Nano Banana 2 Lite',
    provider: 'Google',
    models: 'perfil.premiumModelos',
    // Sin repetir quién factura: eso ya lo dice la línea de arriba, que sale del proveedor.
    caveat: 'perfil.premiumOjo',
  },
  unrestricted_seedream: {
    label: 'Libre · Seedream',
    short: 'Seedream 4.5',
    provider: 'fal',
    models: 'perfil.seedreamModelos',
    recommended: true,
    caveat: 'perfil.seedreamOjo',
  },
  unrestricted_seedream5: {
    label: 'Libre 5.0',
    short: 'Seedream 5.0 [pro]',
    provider: 'fal',
    models: 'perfil.seedream5Modelos',
    caveat: 'perfil.seedream5Ojo',
  },
};

/** Para qué se usó cada llamada. Lo que ordena la tabla de consumo. */
const OPERATION_LABEL: Record<string, Clave> = {
  image: 'op.image',
  video: 'op.video',
  text: 'op.text',
  vision: 'op.vision',
  moderation: 'op.moderation',
};

/** El tramo gratuito diario de Workers AI. Duplicado del dominio, como el resto de esta pantalla. */
const NEURONAS_GRATIS_POR_DIA = 10_000;

export function Settings() {
  const { t } = useT();
  const usage = useApi<UsageData>('/api/usage', 30_000);
  const settings = useApi<SettingsData>('/api/settings');
  const [saving, setSaving] = useState(false);

  if (usage.loading && !usage.data) return <Loading />;
  if (usage.error) return <ErrorBanner error={usage.error} onRetry={usage.reload} />;
  if (!usage.data || !settings.data) return <Loading />;

  // Se extraen a constantes locales: dentro de los callbacks del `map`, TypeScript pierde
  // el estrechamiento que hizo la guarda de arriba sobre la propiedad del objeto.
  const usageData = usage.data;
  const settingsData = settings.data;

  /** Si el perfil activo se factura por Workers AI, que sí regala créditos por día. */
  const currentProfileHasFreeTier =
    usageData.profiles.find((entry) => entry.profile === usageData.profile)?.provider ===
    'workers-ai';

  /**
   * Guarda ajustes y refresca lo que dependa de ellos.
   *
   * Concentra el `patch` + los dos `reload` que antes se repetían suelto en cada botón: el de
   * «volver al modo gratis» manda tres campos a la vez, y el servidor rechaza la combinación
   * contradictoria, así que mandarlos por separado fallaría a mitad de camino.
   */
  const guardar = async (patch: Record<string, unknown>): Promise<void> => {
    setSaving(true);
    try {
      await api.patch('/api/settings', patch);
      settings.reload();
      usage.reload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold tracking-tight">{t('consumo.titulo')}</h1>

      {/*
        El gasto va antes que la cuota, y sólo cuando hay gasto que mostrar.
        
        Con el modo USD 0 activo todo es gratuito y un contador en cero es ruido. Habilitado,
        es lo primero que se busca al entrar — y hasta ahora el panel mostraba el tope mensual
        sin ningún lugar donde ver cuánto llevabas encima. Un presupuesto sin contador es una
        intención, no un control.
      */}
      {!settingsData.settings.zeroSpendMode && (
        <Card
          title={t('uso.gasto')}
          accent={
            usageData.spend.monthlyBudgetUsd > 0 &&
            usageData.spend.monthUsd >= usageData.spend.monthlyBudgetUsd * 0.8
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-3xl font-semibold tabular-nums">
                {formatUsd(usageData.spend.monthUsd)}
              </p>
              <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
                {t('uso.esteMes')}
                {usageData.spend.monthlyBudgetUsd > 0
                  ? t('uso.deTope', { monto: formatUsd(usageData.spend.monthlyBudgetUsd) })
                  : t('uso.sinTope')}
              </p>
              {usageData.spend.monthlyBudgetUsd > 0 && (
                <div className="mt-2 max-w-xs">
                  <QuotaBar
                    used={usageData.spend.monthUsd}
                    limit={usageData.spend.monthlyBudgetUsd}
                  />
                </div>
              )}
            </div>

            <div>
              <p className="text-3xl font-semibold tabular-nums">
                {formatUsd(usageData.spend.dayUsd)}
              </p>
              <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
                {t('uso.hoyConsumidos', {
                  creditos: formatCredits(usageData.spend.dayCredits),
                  unidad: CREDITS_LABEL,
                })}
              </p>
            </div>
          </div>

          {/*
            La distinción que evita un susto: lo de Workers AI entra en el tramo gratuito
            diario hasta agotarlo, así que su costo es cero mientras no se pase. Lo de Google
            se factura desde la primera imagen. Un solo número los mezclaría y haría parecer
            gasto real algo que todavía no lo es.
          */}
          {/*
            De dónde sale la diferencia entre lo consumido y lo cobrado.

            Sin esto, ver «USD 0,00 este mes» junto a miles de créditos consumidos parece un
            contador roto. El tramo gratuito es la explicación, y decirla acá evita tanto el
            susto de creer que se gastó como la sorpresa del día que empiece a cobrarse.
          */}
          <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
            {t('uso.sinTramoGratuitoAntes')}
            <strong>{formatUsd(usageData.spend.monthWithoutFreeTierUsd)}</strong>
            {t('uso.sinTramoGratuitoMedio')}
            <strong>{formatUsd(usageData.spend.billedFromFirstCallUsd)}</strong>
            {t('uso.sinTramoGratuitoFin')}
          </p>
        </Card>
      )}

      {/*
        Se llamaba «Cuota de Workers AI» y las dos mitades eran falsas.

        Ni el tope es de Cloudflare —es el que fijás abajo, en «Tope de gasto diario»— ni los
        créditos son de Workers AI: la barra suma todo lo que se generó, y con el perfil
        Premium eso es enteramente Google. Se leía «109.923 / 150.000 créditos de Workers AI»
        junto a un gasto de USD 1,18 en el que Workers AI no participó.

        El título dice ahora de quién es el tope, y la nota aclara qué cuenta la barra.
      */}

      <div className="mt-8 first:mt-0">
        <h2 className="text-lg font-semibold">{t('uso.loQueSeGasto')}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('uso.soloParaMirar')}</p>
      </div>

      <Card title={t('uso.consumoDeHoy')}>
        {/*
          Cuando el proveedor cortó, mostrar «5.866 / 9.500» sería mentir: sugiere 3.634
          disponibles que no existen. El ledger es una estimación y se midió que va corta,
          así que en ese caso se muestra el hecho —agotada— y el número estimado queda como
          dato secundario, explicando la discrepancia en vez de esconderla.
        */}
        {usageData.quota.providerExhausted ? (
          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-[var(--color-warn)]">
                {t('uso.cuotaAgotada')}
              </span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {t('uso.reinicia', { cuando: formatDateTime(usageData.quota.resetsAt) })}
              </span>
            </div>
            <QuotaBar used={1} limit={1} />
            <p className="mt-3 text-sm">
              {t(currentProfileHasFreeTier ? 'uso.cloudflare' : 'uso.elProveedor')}
              {t('uso.dejoDeAceptarAntes')}
              <strong className="tabular-nums">{formatCredits(usageData.quota.used)}</strong>
              {t('uso.dejoDeAceptarDe')}
              <span className="tabular-nums">{formatCredits(usageData.quota.limit ?? 0)}</span>{' '}
              {CREDITS_LABEL}
              {t('uso.dejoDeAceptarFin')}
            </p>
            <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
              {t('uso.estimacionPrevia')}
            </p>
          </div>
        ) : usageData.quota.limit === null ? (
          /*
            Sin tope no hay barra, porque no hay fracción que dibujar.

            Una barra necesita un denominador. Poner una llena, o vacía, o al 1 % contra un
            número inventado sería la misma clase de mentira que veníamos sacando de esta
            pantalla: un dibujo que afirma una proporción que nadie definió. Lo que sí hay es el
            consumo del día, y eso es lo que se muestra.
          */
          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm tabular-nums">
                {t('uso.creditosHoy', {
                  creditos: formatCredits(usageData.quota.used),
                  unidad: CREDITS_LABEL,
                  usd: formatUsd((usageData.quota.used / 1000) * 0.011),
                })}
              </span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {t('uso.reinicia', { cuando: formatDateTime(usageData.quota.resetsAt) })}
              </span>
            </div>
            <p className="mt-2 text-sm font-medium text-[var(--color-warn)]">
              {t('uso.sinTopeDiario')}
            </p>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {t('uso.sinTopeDiarioAntes')}
              <strong>{t('uso.topeDeGastoDiario')}</strong>
              {t('uso.sinTopeDiarioDespues')}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-sm tabular-nums">
                {t('uso.estimados', {
                  usados: formatCredits(usageData.quota.used),
                  limite: formatCredits(usageData.quota.limit),
                  unidad: CREDITS_LABEL,
                })}
              </span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {t('uso.reinicia', { cuando: formatDateTime(usageData.quota.resetsAt) })}
              </span>
            </div>
            <QuotaBar used={usageData.quota.used} limit={usageData.quota.limit ?? 0} />

            {/*
              Qué es cada número, porque ninguno de los dos es de Cloudflare.

              El tope lo fijaste vos y la barra cuenta todos los proveedores. Sin decirlo, un
              tope de 150.000 al lado de «Workers AI» se leía como una cuota que Cloudflare
              otorga — y con Premium, Cloudflare no interviene en absoluto.
            */}
            <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
              {t('uso.creditosSonAntes')}
              <strong>
                {PROFILE_COPY[usageData.profile]?.provider ?? t('uso.proveedorActivo')}
              </strong>
              {t('uso.creditosSonMedio')}
              <span className="tabular-nums">{formatCredits(usageData.quota.limit ?? 0)}</span>
              {t('uso.creditosSonFin')}
              <strong>{t('uso.topeDeGastoDiario')}</strong>.
              {t(currentProfileHasFreeTier ? 'uso.primeras10k' : 'uso.sinTramoGratuito')}
            </p>
          </>
        )}
      </Card>

      <Card title={t('uso.porProveedor')}>
        {usageData.byProvider.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">{t('uso.sinConsumo')}</p>
        ) : (
          /*
            Seis columnas no entran en un teléfono, y deslizarlas es peor que apilarlas: el
            encabezado se va de pantalla antes que el dato. Debajo de `md` esto es una tarjeta
            por fila con cada cifra rotulada. Mismo componente que la comparativa de plataformas.
          */
          <TablaResponsiva
            filas={usageData.byProvider}
            claveDe={(row) => `${row.provider}-${row.model}`}
            anchoMinimo="34rem"
            columnas={[
              {
                clave: 'para',
                encabezado: t('uso.paraQue'),
                titulo: true,
                /*
                  La operación primero, que es lo que vuelve legible la tabla. Sin ella,
                  `flux-2` y `gemini-3.6-flash` aparecían mezclados en una lista ordenada por
                  créditos, y no se sabía si el gasto se fue en generar imágenes o en escribir
                  captions.
                */
                celda: (row) => {
                  const clave = OPERATION_LABEL[row.operation];
                  return clave ? t(clave) : row.operation;
                },
              },
              {
                clave: 'proveedor',
                encabezado: 'Proveedor',
                celda: (row) => (
                  <span className="text-[var(--color-ink-muted)]">{row.provider}</span>
                ),
              },
              {
                clave: 'modelo',
                encabezado: 'Modelo',
                celda: (row) => (
                  <span className="font-mono text-xs">{row.model.split('/').pop()}</span>
                ),
              },
              {
                clave: 'creditos',
                encabezado: t('uso.creditos'),
                numerica: true,
                celda: (row) => row.neurons.toFixed(0),
              },
              {
                clave: 'llamadas',
                encabezado: t('uso.llamadas'),
                numerica: true,
                celda: (row) => row.calls,
              },
              {
                clave: 'fallos',
                encabezado: t('uso.fallos'),
                numerica: true,
                celda: (row) =>
                  row.failures > 0 ? (
                    <span className="text-[var(--color-danger)]">{row.failures}</span>
                  ) : (
                    '0'
                  ),
              },
            ]}
          />
        )}
      </Card>

      {usageData.media.backend === 'kv' && (
        <Card title={t('uso.almacenamiento')}>
          <p className="text-sm">
            <strong className="tabular-nums">{usageData.media.objects ?? 0}</strong>
            {t('uso.imagenesPunto')}
            <strong className="tabular-nums">
              {((usageData.media.bytes ?? 0) / 1_048_576).toFixed(1)}
            </strong>
            {t('uso.mbDe')}
            <span className="tabular-nums">1024</span>
            {t('uso.disponibles')}
          </p>
          <QuotaBar used={usageData.media.bytes ?? 0} limit={1_073_741_824} />
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">
            {t('uso.enKv')}
          </p>
          <p className="mt-2 text-xs text-[var(--color-ink-faint)]">
            {t('uso.siLlegasAlLimite')}
          </p>
        </Card>
      )}

      <div className="mt-8 first:mt-0">
        <h2 className="text-lg font-semibold">{t('uso.losFrenos')}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('uso.losFrenosQue')}</p>
      </div>

      <Card title={t('freno.modoCero')}>
        <p className="text-sm">
          {t('freno.estadoAntes')}
          <strong>
            {t(settingsData.settings.zeroSpendMode ? 'freno.activo' : 'freno.desactivado')}
          </strong>
        </p>
        <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
          {t('freno.modoCeroQue')}
        </p>
        {/*
          El interruptor gira para los dos lados.

          Tenía botón sólo cuando el modo estaba activo, así que desactivarlo era un viaje de
          ida: el servidor aceptaba volver —sólo apagarlo pide confirmación— pero la pantalla no
          lo ofrecía. Volver a lo seguro tiene que ser más fácil que salir de ahí, no imposible.
        */}
        <div className="mt-4">
          {settingsData.settings.zeroSpendMode ? (
            <Button
              variant="danger"
              disabled={saving}
              onClick={() => {
                if (
                  window.confirm(
                    t('freno.confirmarDesactivar'),
                  )
                ) {
                  void guardar({ zeroSpendMode: false, confirmSpendingEnabled: true });
                }
              }}
            >
              {t('freno.desactivarModoCero')}
            </Button>
          ) : (
            <>
              {/*
                Vuelve **todo** junto, en un solo pedido.

                Encender el modo USD 0 dejando el perfil en uno que cobra no devuelve a gratis:
                deja el perfil apuntando a un proveedor pago y el guardián frenando cada toma.
                El servidor ahora rechaza esa combinación, así que la transición tiene que ser
                atómica o no pasa. Y el tope diario vuelve al tramo gratuito por lo mismo: un
                tope que promete más de lo que el modo permite es un número que miente.
              */}
              <Button
                disabled={saving}
                onClick={() => {
                  if (
                    window.confirm(
                      t('freno.confirmarVolver'),
                    )
                  ) {
                    void guardar({
                      zeroSpendMode: true,
                      imageQualityProfile: 'balanced',
                      dailyNeuronLimit: NEURONAS_GRATIS_POR_DIA,
                    });
                  }
                }}
              >
                {t(saving ? 'freno.volviendo' : 'freno.volverAlModoGratis')}
              </Button>
              <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
                {t('freno.volverQueAntes')}
                <strong>fal</strong>
                {t('freno.volverQueY')}
                <strong>Gemini</strong>
                {t('freno.volverQueDespues')}
              </p>
            </>
          )}
        </div>

        {/*
          El tope diario ES el presupuesto, y por eso vive acá.

          Cloudflare no ofrece tope de gasto: lo único configurable de su lado es el límite de
          CPU por invocación. Así que éste es el único freno real que existe, y estaba
          escondido en una variable de entorno con un máximo de 10.000 —el tramo gratuito—,
          o sea inutilizable en cuanto se pagara.

          Se muestra en dólares porque es la unidad en la que se decide un presupuesto. Las
          créditos quedan al lado para quien quiera contrastarlos con el consumo.
        */}
        {!settingsData.settings.zeroSpendMode && (
          <div className="mt-6 border-t border-[var(--color-border-subtle)] pt-4">
            {/*
              El texto se escribió cuando Workers AI era el único proveedor, y arrancaba
              explicando qué no permite Cloudflare. Con un perfil que factura Google, esa
              aclaración habla de alguien que no interviene — y hace parecer que el tope es de
              ellos cuando es nuestro y cubre a los dos.
            */}
            <p className="text-sm font-medium">{t('uso.topeDeGastoDiario')}</p>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {t('freno.topeDiarioQue')}
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/*
                Los atajos son atajos, no el menú completo.

                Antes eran las cuatro únicas opciones y el techo estaba en 500.000 —unos USD 5,50
                al día—, así que la pantalla decidía cuánto se podía gastar en vez de preguntarlo.
                El presupuesto es de quien paga: los botones cubren los casos comunes y el campo
                de al lado cubre el resto.
              */}
              {[10_000, 50_000, 150_000, 500_000, 2_000_000, 5_000_000].map((limite) => (
                <button
                  key={limite}
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    void api
                      .patch('/api/settings', { dailyNeuronLimit: limite })
                      .then(() => settings.reload());
                  }}
                  className={
                    'rounded-xl border px-3 py-2 text-sm transition ' +
                    (settingsData.settings.dailyNeuronLimit === limite
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] font-medium'
                      : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                  }
                >
                  {/*
                    «Gratis» sólo vale si el proveedor tiene tramo gratuito. Con un perfil que
                    factura desde la primera imagen, 10.000 créditos son dinero, y la etiqueta
                    haría creer lo contrario justo donde se decide cuánto gastar.
                  */}
                  <span className="block tabular-nums">
                    {limite <= 10_000 && currentProfileHasFreeTier
                      ? 'gratis'
                      : `~USD ${((limite / 1000) * 0.011).toFixed(2)}`}
                  </span>
                  <span className="block text-xs text-[var(--color-ink-muted)] tabular-nums">
                    {formatCredits(limite)} {CREDITS_LABEL}
                  </span>
                </button>
              ))}
            </div>

            {/*
              El campo libre, en dólares.

              Es la unidad en la que se piensa un presupuesto; los créditos son la unidad en la
              que se mide el consumo. Convertir acá evita que quien decide tenga que hacer la
              cuenta, que es exactamente donde se cuela un cero de más.
            */}
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="text-xs text-[var(--color-ink-muted)]">
                <span className="mb-1 block">{t('freno.escribiEnDolares')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">USD</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    disabled={saving}
                    defaultValue={((settingsData.settings.dailyNeuronLimit / 1000) * 0.011).toFixed(
                      2,
                    )}
                    onBlur={(event) => {
                      const usd = Number(event.currentTarget.value);
                      if (!Number.isFinite(usd) || usd < 0) return;

                      // El techo del servidor son 20.000.000 de créditos. Recortar acá evita un
                      // 400 que no explicaría nada; el aviso de abajo dice qué pasó.
                      const creditos = Math.min(20_000_000, Math.round((usd / 0.011) * 1000));
                      if (creditos === settingsData.settings.dailyNeuronLimit) return;

                      void api
                        .patch('/api/settings', { dailyNeuronLimit: creditos })
                        .then(() => settings.reload());
                    }}
                    className="w-28 rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm tabular-nums"
                  />
                  <span className="text-xs text-[var(--color-ink-muted)] tabular-nums">
                    = {formatCredits(settingsData.settings.dailyNeuronLimit)} {CREDITS_LABEL}
                  </span>
                </div>
              </label>

              {/*
                Quitar el tope se puede, y por eso está acá y no escondido.

                Pide confirmación por lo mismo que la pide desactivar el modo USD 0: es la única
                barrera de gasto que existe, ningún proveedor ofrece una propia. Confirmar no es
                desaconsejar — es que no ocurra por un clic al pasar.
              */}
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  if (settingsData.settings.dailyNeuronLimit === 0) return;
                  const gastoDelMes = (usageData.spend?.monthUsd ?? 0).toFixed(2);
                  if (
                    !window.confirm(
                      t('freno.confirmarQuitarTope', { gasto: gastoDelMes }),
                    )
                  ) {
                    return;
                  }
                  void api
                    .patch('/api/settings', { dailyNeuronLimit: 0 })
                    .then(() => settings.reload());
                }}
                className={
                  'rounded-xl border px-3 py-2 text-sm transition ' +
                  (settingsData.settings.dailyNeuronLimit === 0
                    ? 'border-[var(--color-warn)] bg-[var(--color-warn)]/10 font-medium text-[var(--color-warn)]'
                    : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                }
              >
                {t(
                  settingsData.settings.dailyNeuronLimit === 0
                    ? 'freno.sinTopeActivo'
                    : 'freno.sinTope',
                )}
              </button>
            </div>

            {/*
              La cifra que importa no es el tope sino cuántas influencers permite con el perfil
              que está puesto. Antes citaba un número escrito a mano del perfil Max — que puede
              no ser el activo, y que quedaría viejo en cuanto cambie una tarifa.
            */}
            {(() => {
              const activo = usageData.profiles.find(
                (entry) => entry.profile === usageData.profile,
              );
              const tope = settingsData.settings.dailyNeuronLimit;
              const porInfluencer = activo?.neuronsPerCreator ?? 0;
              const entran = porInfluencer > 0 ? Math.floor(tope / porInfluencer) : 0;

              /*
                Sin tope no hay «cuántas entran»: no es que entren infinitas, es que el tope dejó
                de ser lo que decide. Decir un número acá —cualquiera— sería responder una
                pregunta que ya no se está haciendo.
              */
              if (tope === 0) {
                return (
                  <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
                    {t('freno.sinTopeDiarioAntes')}
                    <strong>{PROFILE_COPY[usageData.profile]?.short ?? usageData.profile}</strong>
                    {t('freno.cadaInfluencerCuesta')}
                    <strong className="tabular-nums">{formatCredits(porInfluencer)}</strong>{' '}
                    {CREDITS_LABEL}
                    {t('freno.yNadaLimita', {
                      usd: formatUsd((porInfluencer / 1000) * 0.011),
                    })}
                  </p>
                );
              }

              return (
                <p className="mt-3 text-xs">
                  <span className="text-[var(--color-ink-muted)]">
                    {t('freno.hoyAntes')}
                    <strong className="tabular-nums">{formatCredits(tope)}</strong>{' '}
                    {CREDITS_LABEL}
                    {t('freno.porDiaConPerfil')}
                    <strong>{PROFILE_COPY[usageData.profile]?.short ?? usageData.profile}</strong>
                    {t('freno.cadaInfluencerCuesta')}
                    <strong className="tabular-nums">{formatCredits(porInfluencer)}</strong>.
                  </span>{' '}
                  {entran > 0 ? (
                    <span className="text-[var(--color-ink-muted)]">
                      {t('freno.entranPorDia', { n: entran })}
                    </span>
                  ) : (
                    <span className="font-medium text-[var(--color-warn)]">
                      {t('freno.noAlcanza')}
                    </span>
                  )}
                </p>
              );
            })()}

            {/*
              El tope mensual, que es el que se piensa en dinero.

              El diario protege de un día malo; no protege de treinta días gastando el máximo.
              Con 150.000 diarias son unos USD 50 al mes sin que ningún límite se cruce nunca.
            */}
            <p className="mt-5 text-sm font-medium">{t('freno.topeMensual')}</p>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{t('freno.topeMensualQue')}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {[0, 5, 10, 25, 50, 100, 250].map((tope) => (
                <button
                  key={tope}
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    void api
                      .patch('/api/settings', { monthlyBudgetUsd: tope })
                      .then(() => settings.reload());
                  }}
                  className={
                    'rounded-xl border px-3 py-2 text-sm transition ' +
                    (settingsData.settings.monthlyBudgetUsd === tope
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] font-medium'
                      : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                  }
                >
                  {tope === 0 ? t('freno.sinTopeMinuscula') : `USD ${tope}`}
                </button>
              ))}

              {/* El mismo campo libre que el diario, por el mismo motivo: el presupuesto es tuyo. */}
              <label className="ml-1 text-xs text-[var(--color-ink-muted)]">
                <span className="mb-1 block">{t('freno.oEscribilo')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">USD</span>
                  <input
                    type="number"
                    min={0}
                    step={5}
                    disabled={saving}
                    defaultValue={settingsData.settings.monthlyBudgetUsd}
                    onBlur={(event) => {
                      const usd = Number(event.currentTarget.value);
                      if (!Number.isFinite(usd) || usd < 0) return;
                      const tope = Math.min(10_000, usd);
                      if (tope === settingsData.settings.monthlyBudgetUsd) return;
                      void api
                        .patch('/api/settings', { monthlyBudgetUsd: tope })
                        .then(() => settings.reload());
                    }}
                    className="w-24 rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm tabular-nums"
                  />
                </div>
              </label>
            </div>

            {settingsData.settings.monthlyBudgetUsd === 0 && (
              <p className="mt-2 text-xs text-[var(--color-warn)]">
                {settingsData.settings.dailyNeuronLimit === 0 ? (
                  /*
                    Los dos topes en cero es el único estado sin ningún freno de gasto, y hay que
                    nombrarlo así. Antes esta rama habría dicho «treinta días suman ~USD 0,00»,
                    que es la lectura más tranquilizadora posible de no tener ningún límite.
                  */
                  <>
                    {t('freno.sinNingunFreno')}
                  </>
                ) : (
                  <>
                    {t('freno.sinTopeMensual', {
                      monto: (
                        (settingsData.settings.dailyNeuronLimit / 1000) *
                        0.011 *
                        30
                      ).toFixed(2),
                    })}
                  </>
                )}
              </p>
            )}
          </div>
        )}
      </Card>

      <div className="mt-8 first:mt-0">
        <h2 className="text-lg font-semibold">{t('freno.herramientas')}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-ink-muted)]">{t('freno.herramientasQue')}</p>
      </div>

      <Card title={t('freno.featureFlags')}>
        <ul className="space-y-3">
          {settingsData.flags.map((flag) => (
            <li key={flag.key} className="flex items-start justify-between gap-4 text-sm">
              <div className="min-w-0">
                <p className="font-mono text-xs">{flag.key}</p>
                <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">{flag.description}</p>
              </div>
              <div className="shrink-0 text-right">
                <span
                  className={
                    flag.enabled
                      ? 'text-xs font-medium text-[var(--color-ok)]'
                      : 'text-xs text-[var(--color-ink-muted)]'
                  }
                >
                  {t(flag.enabled ? 'freno.activo' : 'freno.inactivo')}
                </span>
                {/* Un flag que puede costar dinero se ignora mientras el modo USD 0 esté activo. */}
                {flag.canIncurCost && (
                  <span className="mt-0.5 block text-xs text-[var(--color-warn)]">
                    {t('freno.puedeGenerarCargos')}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

    </div>
  );
}
